import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleSchema, absoluteUrl } from '@/seo/schema';

interface Step {
  title: string;
  text: string;
}

interface FaqItem {
  q: string;
  a: string;
}

interface TableData {
  title: string;
  headers: string[];
  rows: string[][];
}

const PUBLISH_DATE = new Date('2026-01-15');
const UPDATE_DATE = new Date('2026-08-21');

export default function ArticlePage() {
  const { t, i18n } = useTranslation();

  const articleSchema = buildArticleSchema({
    headline: t('article.h1'),
    description: t('article.metaDescription'),
    url: absoluteUrl('/usfans-spreadsheet-guide'),
    datePublished: PUBLISH_DATE.toISOString(),
    dateModified: UPDATE_DATE.toISOString(),
  });

  const intro = t('article.intro', { returnObjects: true }) as string[];
  const whatBody = t('article.what.body', { returnObjects: true }) as string[];
  const whyBody = t('article.why.body', { returnObjects: true }) as string[];
  const steps = t('article.how.steps', { returnObjects: true }) as Step[];
  const checks = t('article.checks.items', { returnObjects: true }) as string[];
  const table = t('article.table', { returnObjects: true }) as TableData;
  const faqs = t('article.faq.items', { returnObjects: true }) as FaqItem[];

  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };

  return (
    <>
      <title>{t('article.metaTitle')}</title>
      <meta name="description" content={t('article.metaDescription')} />
      <JsonLd data={articleSchema} />
      <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              {t('common.brand')}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{t('common.nav.spreadsheet')}</span>
          </nav>

          <h1 className="mt-6 text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
            {t('article.h1')}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {t('common.published')}: {PUBLISH_DATE.toLocaleDateString(i18n.language, dateOptions)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-refresh-line"></i>
              {t('common.updated')}: {UPDATE_DATE.toLocaleDateString(i18n.language, dateOptions)}
            </span>
          </div>

          <div className="mt-6 space-y-4">
            {intro.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {p}
              </p>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.what.title')}
            </h2>
            <div className="mt-4 space-y-4">
              {whatBody.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.why.title')}
            </h2>
            <div className="mt-4 space-y-4">
              {whyBody.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.how.title')}
            </h2>
            <div className="mt-5 flex flex-col gap-4">
              {steps.map((step, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-lg border border-background-200 bg-background-50">
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950">{step.title}</h3>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.checks.title')}
            </h2>
            <ul className="mt-5 flex flex-col gap-3">
              {checks.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                  <i className="ri-checkbox-circle-line text-lg text-primary-500 mt-0.5"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {table.title}
            </h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full text-left text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-background-100">
                    {table.headers.map((h, i) => (
                      <th key={i} className="px-4 py-3 font-semibold text-foreground-900 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, i) => (
                    <tr key={i} className="border-t border-background-200">
                      {row.map((cell, j) => (
                        <td key={j} className={`px-4 py-3 ${j === 0 ? 'font-medium text-foreground-900' : 'text-foreground-600'}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.faq.title')}
            </h2>
            <div className="mt-5 flex flex-col gap-3">
              {faqs.map((faq, i) => (
                <div key={i} className="p-5 rounded-lg border border-background-200 bg-background-50">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900">{faq.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
              {t('article.conclusion.title')}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              {t('article.conclusion.text')}
            </p>
          </section>

          <div className="mt-10 p-6 rounded-lg bg-primary-50 border border-primary-200">
            <p className="text-sm text-foreground-700">
              <Link to="/" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                {t('article.backCta')}
              </Link>{' '}
              ·{' '}
              <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                {t('article.cta')}
              </a>
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('common.backHome')}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
      </div>
    </>
  );
}